<template>
  <section class="testimonials-section" aria-labelledby="testimonials-title">
    <div class="testimonials-inner">
      <h2 id="testimonials-title" class="title u-display">
        {{ $t('home.testimonials.title') }}
      </h2>
      <!-- role="list" is not redundant here: .testimonials-container sets
           list-style: none, and WebKit drops the implicit list role when a list
           has no marker, so VoiceOver would announce neither "2 items" nor the
           position. -->
      <ul class="testimonials-container" role="list">
        <li
          v-for="testimonial in testimonials"
          :key="testimonial.nameKey"
          class="testimonial-card"
        >
          <blockquote class="quote">
            <p class="quote-text">{{ $t(testimonial.quoteKey) }}</p>
          </blockquote>
          <div class="testimonial-author">
            <!-- Decorative: the initial only repeats the name rendered directly
                 below it, so it is hidden rather than announced twice. -->
            <span
              :class="['author-avatar', testimonial.avatarClass]"
              aria-hidden="true"
            >
              {{ initialOf($t(testimonial.nameKey)) }}
            </span>
            <p class="author-name">{{ $t(testimonial.nameKey) }}</p>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
export default {
  name: "TestimonialsSection",
  data() {
    return {
      // Literal keys rather than keys built in the template: a constructed key
      // is invisible to grep and to any i18n key extractor.
      testimonials: [
        {
          quoteKey: "home.testimonials.items.haylee.quote",
          nameKey: "home.testimonials.items.haylee.name",
          avatarClass: "avatar-orange",
        },
        {
          quoteKey: "home.testimonials.items.liz.quote",
          nameKey: "home.testimonials.items.liz.name",
          avatarClass: "avatar-blue",
        },
      ],
    };
  },
  methods: {
    // Array.from, not charAt: it splits by code point, so a name starting with
    // an astral character yields a whole glyph rather than half a surrogate
    // pair.
    initialOf(name) {
      return Array.from(String(name ?? ""))[0] || "";
    },
  },
};
</script>

<style scoped>
/* Marketing routes are light-mode only (the pre-paint stamp in
   public/index.html gates dark on /dashboard/ and native), so this section has
   no dark-mode branch and stays on the hue tokens the sibling landing sections
   use rather than the role-token layer. */

/* width: 100%, never 100vw: 100vw includes the scrollbar gutter, which is what
   put a horizontal scrollbar on this page in the first place.

   The band is --al-bg-light while LanguagesSection directly above is the darker
   --al-surface-muted and PricingSection directly below is white, so this
   section keeps a visible boundary on both sides. */
.testimonials-section {
  width: 100%;
  background: var(--al-bg-light);
}

.testimonials-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 72px 32px;
}

/* DESIGN.md type scale, H2: 26px / 1.16 / 700. It is not breakpoint-split, so
   there is no mobile size override below. */
.title {
  font-size: 26px;
  /* public/index.html loads Fraunces at discrete 600/700/800. */
  font-weight: 700;
  line-height: 1.16;
  color: var(--al-ink-900);
  text-align: center;
  margin: 0 0 40px;
  overflow-wrap: break-word;
}

.testimonials-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  list-style: none;
  margin: 0;
  padding: 0;
}

/* DESIGN.md's card recipe: white surface, subtle --al-gray-300 hairline, small
   shadow, radius 16px. No hover lift: these cards are quotes, not click
   targets, and a transform on hover would advertise one that does not exist. */
.testimonial-card {
  background: var(--al-white);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-sm);
  padding: 28px;
  display: flex;
  flex-direction: column;
  /* Long unbroken words in a locale must not force the grid track wider. */
  min-width: 0;
  overflow-wrap: break-word;
}

.quote {
  margin: 0;
  flex: 1;
}

/* DESIGN.md type scale, Body: 16px / 1.55 / 400. */
.quote-text {
  font-size: 16px;
  line-height: 1.55;
  color: var(--al-gray-700);
  margin: 0;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.author-avatar {
  display: flex;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--al-radius-pill);
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  color: var(--al-white);
}

.author-avatar.avatar-orange {
  background-color: var(--al-orange);
  /* AA: white on --al-orange is ~2.6:1; ink passes at ~6.7:1. */
  color: var(--al-ink-900);
}

.author-avatar.avatar-blue {
  /* AA: white on --al-primary is ~3.3:1; primary-hover passes at ~4.67:1. */
  background-color: var(--al-primary-hover);
}

/* DESIGN.md type scale, Body small: 14px / 1.45 / 400, at label weight. */
.author-name {
  font-size: 14px;
  line-height: 1.45;
  font-weight: 600;
  color: var(--al-ink-900);
  margin: 0;
}

/* Tablet padding matches HeroSection's, HowItWorksSection's and
   LearningModesSection's. */
@media (max-width: 1023px) {
  .testimonials-inner {
    padding: 56px 24px;
  }
}

@media (max-width: 767px) {
  .testimonials-inner {
    padding: 48px 16px;
  }

  .title {
    margin-bottom: 28px;
  }

  .testimonials-container {
    grid-template-columns: 1fr;
  }

  .testimonial-card {
    padding: 24px;
  }
}
</style>
